import { useCallback, useMemo, useState } from "react";

import {
  Color,
  DateCalendar,
  Desk,
  Legend,
  SectionLabel,
  Spacer,
  Text,
  TimePickerInput,
} from "@vericus/cadmus-ui";

import dayjs from "dayjs";

import { TaskFormat } from "@/generated/graphql";
import {
  dateColors,
  DateTimeGrid,
  getHighlightDays,
  GridDate,
  GridTime,
  useJumpToToday,
} from "@/ui/DatePicker";
import {
  applyPickedDay,
  applyPickedTime,
  getLocalDate,
} from "@/utils/datetime/transform-date";

import { Footer } from "./footer";
import { Header } from "./header";
import {
  Step,
  useStepDateBounds,
  validateSettingDraftDueDate,
  validateSettingDueDate,
  validateSettingReturnDate,
} from "./internal";
import { QuickOptions } from "./quick-options";

export type DateSettings = {
  dueDate: string | undefined;
  returnDate: string | undefined;
  draftDueDate: string | undefined;
};

interface TaskTimelineProps {
  /** Current final due date */
  dueDate: string | null;
  /** Current feedback return date */
  returnDate: string | null;
  /** Current draft due date */
  draftDueDate: string | null;
  /** prevents selection of the Draft due date and the display
   * of the Draft Due date selection step.
   */
  noDraft: boolean;
  /** Task format — passed to Footer to suppress the draft step for Multiformat */
  format: TaskFormat;
  /** Callback to update new dates picked in the local Settings state. */
  setDates(dates: {
    dueDate?: string;
    returnDate?: string;
    draftDueDate?: string;
  }): void;
  /** Callback to provide "go back" functionality after user cancel.  */
  onCancel(): void;
  /** Callback to provide "go next" functionality after user confirm date changes */
  onConfirm(dateSettings: DateSettings): void;
  /* Optional starting step, only used if the final 'dueDate' is not null. */
  initialStep?: Step;
}

/**
 * Linear update workflow for Task dates: final due date, feedback return date,
 * and optionally draft due date.
 *
 * Renders date time pickers for each date selection. Date selection is almost
 * linear, but the initial step can be set on mount using the `initialStep`
 * prop. The `Step` type is an enum distinguishing between final, return, and
 * draft due date picking steps.
 *
 * Final and Return dates are set together, while draft due can be set
 * separately. There are certain validation rules enforced on the temporal
 * orderings of the dates. These rules are:
 *
 *   - Return date is after Due Date
 *   - Draft due date is before due date
 *
 * If the validations fail, the offending date is reset to null. This is rarely
 * the case when simply following the linear workflow of setting the
 * Final-Return date and then the Draft date.
 *
 * Time is also selected alongside the date. While the time picker is separate,
 * it is merged with the currently selected date for the currently rendering
 * step.
 */
export function TaskTimeline({
  initialStep,
  setDates,
  onCancel,
  onConfirm,
  noDraft,
  format,
  ...props
}: TaskTimelineProps) {
  // State for current selection step
  const [step, setStep] = useState(() =>
    initialStep && props.dueDate ? initialStep : Step.Final
  );

  // Local states of the incoming dates.
  const [dueDate, setDueDate] = useState<Date | null>(
    props.dueDate ? getLocalDate(props.dueDate) : null
  );
  const [returnDate, setReturnDate] = useState<Date | null>(
    props.returnDate ? getLocalDate(props.returnDate) : null
  );
  const [draftDueDate, setDraftDueDate] = useState<Date | null>(
    props.draftDueDate ? getLocalDate(props.draftDueDate) : null
  );

  // validation
  const [errorMessage, setErrorMessage] = useState<undefined | string>(
    undefined
  );

  // Date value, updater, and legend-matching selected colour for the
  // currently active step.
  let date;
  let setDate: (d: Date | null) => void;
  let selectedColor: Color;
  switch (step) {
    case Step.Final:
      date = dueDate;
      selectedColor = dateColors.dueDate;
      setDate = (d) => {
        setDueDate(d);
        if (d) {
          validateSettingDueDate(
            d,
            (e) => setErrorMessage(e),
            () => setErrorMessage(undefined)
          );
        }
      };
      break;
    case Step.Return:
      date = returnDate;
      selectedColor = dateColors.returnDate;
      setDate = (d) => {
        setReturnDate(d);
        if (d && dueDate) {
          validateSettingReturnDate(
            dueDate,
            d,
            (e) => setErrorMessage(e),
            () => setErrorMessage(undefined)
          );
        }
      };
      break;
    case Step.Draft:
      date = draftDueDate;
      selectedColor = dateColors.draftDueDate;
      setDate = (d) => {
        setDraftDueDate(d);

        if (d && dueDate) {
          validateSettingDraftDueDate(
            dueDate,
            d,
            (e) => setErrorMessage(e),
            () => setErrorMessage(undefined)
          );
        }
      };
      break;
  }

  // Callback to confirm updates to all the currently set dates.
  const handleConfirmDates = useCallback(() => {
    const dates: DateSettings = {
      dueDate: dueDate ? dueDate.toISOString() : undefined,
      returnDate: returnDate ? returnDate.toISOString() : undefined,
      draftDueDate: draftDueDate ? draftDueDate.toISOString() : undefined,
    };
    setDates(dates);
    onConfirm(dates);
  }, [dueDate, returnDate, draftDueDate, setDates, onConfirm]);

  // Days to highlight in the currently display step.
  const highlightDays = useMemo(
    () =>
      getHighlightDays({
        dueDate: step !== Step.Final && dueDate ? dueDate.toISOString() : null,
        returnDate:
          step === Step.Draft && returnDate ? returnDate.toISOString() : null,
      }),
    [step, dueDate, returnDate]
  );

  // Callback to jump the visible month to today's month.
  const [todayKey, jumpToToday] = useJumpToToday();
  // Min/max selectable dates for the current step.
  const { minDate, maxDate } = useStepDateBounds(step, dueDate);

  return (
    <Desk.Layout>
      <Desk.Table>
        <Spacer spacing={90} />
        <Header step={step} />
        <Spacer spacing={45} />
        <DateTimeGrid>
          <GridDate>
            <SectionLabel title="Select a date" />
            <Spacer spacing={24} />
            <DateCalendar
              key={todayKey}
              mode="single"
              selectedColor={selectedColor}
              value={date ? dayjs(date).format("YYYY-MM-DD") : null}
              onChange={(value) => {
                const dateString = value as string | null;
                setDate(
                  dateString != null ? applyPickedDay(dateString, date) : null
                );
              }}
              markedDates={Object.entries(highlightDays).flatMap(
                ([date, colors]) => colors.map((color) => ({ date, color }))
              )}
              minDate={minDate}
              maxDate={maxDate}
            />
            <Spacer spacing={24} />
            <Text kind="headingSix" as="span">
              Quick Options
            </Text>
            <Spacer spacing={9} />
            <QuickOptions
              dueDate={dueDate}
              setDate={setDate}
              step={step}
              jumpToToday={jumpToToday}
            />
            <Spacer spacing={45} />
          </GridDate>
          <GridTime>
            <SectionLabel title="Select a time" />
            <Spacer spacing={24} />
            <TimePickerInput
              disabled={date === null}
              value={
                date
                  ? `${dayjs(date).hour()}:${dayjs(date).minute()}`
                  : undefined
              }
              format="12h"
              onChange={(value) => setDate(applyPickedTime(value, date))}
              defaultValue="23:59"
              errorMessage={errorMessage}
            />
            <Legend
              dueDate={dueDate}
              returnDate={returnDate}
              draftDueDate={noDraft ? undefined : draftDueDate}
            />
          </GridTime>
        </DateTimeGrid>
        <Spacer spacing={108} />
      </Desk.Table>
      <Desk.Drawer>
        <Footer
          step={step}
          setStep={setStep}
          onConfirm={handleConfirmDates}
          onCancel={onCancel}
          noDraft={noDraft}
          format={format}
          disabled={date === null || errorMessage !== undefined}
          dueDate={dueDate}
          returnDate={returnDate}
          draftDueDate={draftDueDate}
        />
      </Desk.Drawer>
    </Desk.Layout>
  );
}
